import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import Select from "react-select";
import { all_routes } from "../../../router/backendComponents/admin/all_routes";
import { isAuthenticated } from "../../../authentication/backendModule/admin/auth";
import apiRequestCall from "../../../apiHelper/globalHelper";
import { parseValidationErrors } from "../../../apiHelper/validationHelper";
import Export from "../../../formHelper/export";
import Search from "../../../formHelper/search";
import Pagination from "../../../formHelper/pagination";
import Table from "../../../formHelper/list";
import DateRangePicker from "react-bootstrap-daterangepicker";
import Success from "../../../formHelper/success";
import Delete from "../../../formHelper/delete";
import FilterDropdown from "../../../formHelper/filterDropDown";
import Loader from "../../../formHelper/Loader";
import exportApiCall from "../../../apiHelper/export";
import dayjs from "dayjs";
import type { RootState, AppDispatch } from "../../../core/data/redux/store";
import { useDispatch, useSelector } from "react-redux";
interface Category {
  id: number;
  name: string;
  description: string;
  parameter_field_id: number[];
  created_at: string;
  parameter_fields?: { id: number; name: string }[];
}

interface ParamField {
  id: number;
  name: string;
}

interface Permission {
  id: number;
  role_id: number;
  name: string;
  role: {
    permissions: Record<string, Record<string, string>>;
  };
}

interface Branch {
  id: number;
  name: string;
}

const AddUpdateCategory: React.FC = () => {
  const backendRoute = all_routes.backend;

  const [list, setList] = useState<Category[]>([]);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    parameter_field_id: [] as number[],
  });
  const [formError, setFormError] = useState<{ [key: string]: string }>({});
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const [paramList, setParamList] = useState<ParamField[]>([]);
  const [branchFilter, setBranchFilter] = useState<number[]>([]);

  const [showForm, setShowForm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showMessage, setShowMessage] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [showDeleteFailModal, setShowDeleteFailModal] = useState(false);

  const [query, setQuery] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(() =>
    typeof window !== "undefined" && window.innerWidth <= 768 ? 5 : 10
  );
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);

  // export
  const [exporting, setExporting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

  // request guards
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  /* ---------- Export helpers ---------- */
  const triggerDownload = (url: string, filename: string, revoke = false) => {
    if (!url) return;

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();

    setDownloading(true);
    setTimeout(() => setDownloading(false), 3000);
    setExporting(false);
  };

  // Poll API for local download
  const pollForFile = (filename: string) => {
    let attempts = 0;
    const maxAttempts = 60;

    const interval = setInterval(async () => {
      attempts++;
      try {
        const res = await exportApiCall("get", `/download-export/${filename}`, {
          responseType: "blob",
        });

        if (res) {
          clearInterval(interval);
          setExporting(false);
          setDownloading(true);

          const blob = new Blob([res], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = window.URL.createObjectURL(blob);
          triggerDownload(url, filename, true);

          setTimeout(() => setDownloading(false), 3000);
        }
      } catch (err: any) {
        if (err?.response?.status !== 404) {
          clearInterval(interval);
          setExporting(false);
          setDownloading(false);
          alert("Export failed while downloading.");
        } else if (attempts >= maxAttempts) {
          clearInterval(interval);
          setExporting(false);
          setDownloading(false);
          alert("Export took too long. Please try again later.");
        }
      }
    }, 5000);
  };

  const exportFile = async () => {
    if (exporting || submitting || deleting) return;
    try {
      setExporting(true);
      setDownloading(false);
      setDownloadUrl(null);

      const result = await apiRequestCall("post", backendRoute.getCategory, {
        data: {
          search: query.trim(),
          start_date: startDate,
          end_date: endDate,
          branch_list: branchFilter,
          download: true,
        },
      });

      const filename = result?.data?.data?.file;
      const fileUrl = result?.data?.data?.url;

      if (!filename) {
        setExporting(false);
        alert("Export request failed.");
        return;
      }

      pollForFile(filename);
    } catch (err) {
      console.error("Export failed:", err);
      setExporting(false);
      setDownloading(false);
      alert("Export failed. Please try again.");
    }
  };

  /* ---------- CRUD helpers ---------- */
  const inputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormError((prev) => {
      const updated = { ...prev };
      delete updated[name];
      return updated;
    });
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({ name: "", description: "", parameter_field_id: [] });
    setFormError({});
    setSelectedId(null);
    setShowForm(false);
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        category_id: selectedId,
        update_status: !!selectedId,
      };

      const result = await apiRequestCall(
        "post",
        backendRoute.addUpdateCategory,
        {
          data: payload,
        }
      );

      if (result?.data?.code === 221) {
        setFormError(parseValidationErrors(result?.data?.errors));
        return;
      }

      if (result?.data?.code === 200) {
        resetForm();
        setShowSuccess(true);
        setShowMessage(result?.data?.message);
        fetchList({ page: currentPage, perPage });
      } else {
        setShowMessage(result?.data?.message || "Operation failed");
        setShowSuccess(true);
      }
    } catch (err) {
      console.error("Submit failed:", err);
      setShowMessage((err as any)?.message || "Submit failed. Please try.");
      setShowSuccess(true);
    } finally {
      setSubmitting(false);
    }
  };

  const updateRecord = (id: number) => {
    if (submitting || deleting) return;
    const selected = list.find((x) => x.id === id);
    if (!selected) return;
    setFormError({});
    setFormData({
      name: selected.name,
      description: selected.description,
      parameter_field_id: selected.parameter_field_id ?? [],
    });
    setSelectedId(id);
    setShowForm(true);
  };

  const deleteRecord = (id: number) => {
    if (submitting || deleting) return;
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    if (deleting) return;
    setDeleting(true);
    try {
      const result = await apiRequestCall("post", backendRoute.deleteCategory, {
        data: { id: deleteId },
      });

      if (result?.data?.code === 221) {
        setShowMessage(result?.data?.errors?.id[0]);
        setShowDeleteFailModal(true);
        return;
      }

      if (result?.data?.code === 200) {
        fetchList({ page: currentPage, perPage });
        setShowMessage(result?.data?.message);
        setShowSuccess(true);
      } else {
        setShowMessage(result?.data?.message || "Delete failed");
        setShowSuccess(true);
      }
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Delete failed. Please try again.");
    } finally {
      setDeleteId(null);
      setShowDeleteModal(false);
      setDeleting(false);
    }
  };

  /* ---------- Fetch list / param fields ---------- */
  const fetchList = async ({ page = 1, perPage: p = 10 } = {}) => {
    setLoading(true);
    try {
      const result = await apiRequestCall("post", backendRoute.getCategory, {
        data: {
          page,
          per_page: p,
          start_date: startDate,
          end_date: endDate,
          search: query.trim(),
          branch_list: branchFilter !== null ? branchFilter : [],
        },
      });

      const res = result?.data?.data;
      if (res) {
        setList(res.data ?? []);
        setCurrentPage(res.current_page ?? 1);
        setPerPage(res.per_page ?? 10);
        setTotalItems(res.total ?? 0);
      } else {
        setList([]);
        setTotalItems(0);
      }
    } catch (err) {
      console.error("Failed to fetch category list:", err);
      setList([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const getParamFields = async () => {
    try {
      const result = await apiRequestCall(
        "post",
        backendRoute.getParameterFieldDD
      );
      const raw = result?.data?.data;
      if (raw && typeof raw === "object") {
        setParamList(
          Object.entries(raw).map(([id, name]) => ({
            id: +id,
            name: String(name),
          }))
        );
      }
    } catch (err) {
      console.error("Failed to fetch parameter fields:", err);
    }
  };

  useEffect(() => {
    fetchList({ page: currentPage, perPage });
  }, [query, currentPage, perPage, startDate, endDate, branchFilter]);

  useEffect(() => {
    getParamFields();
  }, []);

  useEffect(() => {
    const styleId = "category-responsive-styles";
    if (document.getElementById(styleId)) return;

    const css = `
      .page-header { margin-bottom: 0.5rem; }
      .count-title, .count-badge { background: rgba(0,0,0,0.04); padding:4px 8px; border-radius:8px; font-weight:600; }
      .icon-form .form-icon { margin-right:0.6rem; display:flex; align-items:center; position:absolute; left:0.85rem; top:50%; transform:translateY(-50%); pointer-events:none; }
      .card-control .form-control.bookingrange { padding-left: 2.6rem; }
      .filter-dropdown-menu .filter-set-view { min-width:220px; }
      .offcanvas.show { visibility: visible !important; }
    `;

    const node = document.createElement("style");
    node.id = styleId;
    node.appendChild(document.createTextNode(css));
    document.head.appendChild(node);

    return () => {
      const el = document.getElementById(styleId);
      if (el) el.remove();
    };
  }, []);

  const permission = useSelector(
    (state: RootState) => state.rolesPermissions?.data
  ) as Permission | null;

  const permissionMap = useMemo(() => {
    return permission?.role?.permissions ?? {};
  }, [permission]);

  if (!isAuthenticated()) return <Navigate to="/login" replace />;

  /* ---------- Table columns ---------- */
  const columns = [
    {
      key: "id",
      label: "Sr. No",
      render: (_: any, i: number) => (currentPage - 1) * perPage + i + 1,
    },
    { key: "name", label: "Name" },
    { key: "description", label: "Description" },
    { key: "parameter_fields", label: "Parameter Fields" },
    {
      key: "created_at",
      label: "Date",
    },

    ...(permissionMap?.category?.edit_category ||
    permissionMap?.category?.delete_category
      ? [
          {
            key: "action",
            label: "Action",
            render: (row: Category) => (
              <div className="hstack gap-2 fs-15">
                {permissionMap?.category?.edit_category && (
                  <button
                    className="btn btn-icon btn-sm btn-soft-info"
                    onClick={() => updateRecord(row.id)}
                    disabled={submitting || deleting}
                    aria-label={`Edit ${row.name}`}
                    title="Edit"
                  >
                    <i className="feather-edit" />
                  </button>
                )}

                {permissionMap?.category?.delete_category && (
                  <button
                    className="btn btn-icon btn-sm btn-soft-danger"
                    onClick={() => deleteRecord(row.id)}
                    disabled={submitting || deleting}
                    aria-label={`Delete ${row.name}`}
                    title="Delete"
                  >
                    <i className="feather-trash" />
                  </button>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  /* ---------- Render (Source-style layout) ---------- */
  return (
    <div className="page-wrapper">
      <div className="content">
        {/* compact header: title | export | new */}
        <div className="row page-header">
          <div className="col-12">
            <div className="d-flex align-items-start justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <h4 className="page-title">
                  Category
                  <span className="count-title">{totalItems}</span>
                </h4>
              </div>

              <div className="d-flex align-items-center gap-2">
                <div className="d-flex justify-content-end gap-2">
                  {permissionMap?.["category"]?.["export_category"] && (
                    <Export
                      options={[
                        {
                          label: downloading
                            ? "Downloading..."
                            : exporting
                            ? "Generating..."
                            : "Export",
                          iconClass: downloading
                            ? "ti ti-download"
                            : exporting
                            ? "ti ti-loader"
                            : "ti ti-file-type-xls",
                          colorClass:
                            exporting || downloading
                              ? "text-muted"
                              : "text-primary",
                          onClick:
                            exporting || downloading || submitting || deleting
                              ? undefined
                              : exportFile,
                        },
                      ]}
                    />
                  )}

                  {permissionMap?.["category"]?.["add_category"] && (
                    <button
                      className="btn btn-soft-success d-flex align-items-center"
                      onClick={() => {
                        resetForm();
                        setShowForm(true);
                      }}
                      disabled={submitting}
                      aria-label="Add new category"
                    >
                      <i className="ti ti-square-rounded-plus" />
                      <span className="d-none d-md-inline ms-1">New</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Top card: search / date / filter */}
        <div className="card mb-2">
          <div className="card-body">
            <div className="row g-2 align-items-center">
              <div className="col-12 col-md-3">
                <div className="icon-form">
                  <span className="form-icon">
                    <i className="ti ti-search" />
                  </span>
                  <Search
                    placeholder="Search Category"
                    value={query}
                    onChange={setQuery}
                  />
                </div>
              </div>

              <div className="col-12 col-md-3">
                <div className="icon-form">
                  <span className="form-icon">
                    <i className="ti ti-calendar" />
                  </span>
                  <DateRangePicker
                    initialSettings={{
                      autoUpdateInput: false,
                      locale: { cancelLabel: "Clear" },
                    }}
                    onCallback={(start, end) => {
                      setStartDate(start.format("YYYY-MM-DD"));
                      setEndDate(end.format("YYYY-MM-DD"));
                    }}
                    onCancel={() => {
                      setStartDate("");
                      setEndDate("");
                    }}
                  >
                    <input
                      type="text"
                      className="form-control bookingrange"
                      placeholder="Select Date Range Filter"
                      value={
                        startDate && endDate
                          ? `${dayjs(startDate).format("DD-MM-YYYY")} - ${dayjs(
                              endDate
                            ).format("DD-MM-YYYY")}`
                          : ""
                      }
                      readOnly
                      style={{ cursor: "pointer" }}
                      aria-label="Select date range"
                    />
                  </DateRangePicker>
                </div>
              </div>

              <div className="col-12 col-md-3">
                <FilterDropdown
                  label="Branch"
                  endpoint="/getBranchDD"
                  selected={branchFilter}
                  onChange={(v) => setBranchFilter(v as number[])}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Table card */}
        <div className="card">
          <div className="card-body">
            <div className="table-responsive">
              {loading ? <Loader /> : <Table data={list} columns={columns} />}
            </div>
          </div>

          <Pagination
            currentPage={currentPage}
            perPage={perPage}
            totalItems={totalItems}
            onPageChange={(p) => {
              setCurrentPage(p);
              fetchList({ page: p, perPage });
            }}
            onPerPageChange={(pp) => {
              setPerPage(pp);
              setCurrentPage(1);
              fetchList({ page: 1, perPage: pp });
            }}
            perPageOptions={[
              { value: 5, label: "5" },
              { value: 10, label: "10" },
              { value: 25, label: "25" },
            ]}
          />
        </div>

        {/* Offcanvas Form */}
        <div
          className={`offcanvas offcanvas-end offcanvas-large ${
            showForm ? "show" : ""
          }`}
          style={{ visibility: showForm ? "visible" : "hidden" }}
          tabIndex={-1}
          aria-modal={showForm}
          role="dialog"
        >
          <div className="offcanvas-header border-bottom">
            <h5 className="fw-semibold">
              {selectedId ? "Update Category" : "Add Category"}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => !submitting && setShowForm(false)}
              disabled={submitting}
              aria-label="Close"
            >
              <i className="ti ti-x" />
            </button>
          </div>

          <div className="offcanvas-body">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!submitting) handleSubmit();
              }}
            >
              <div className="mb-3">
                <label className="col-form-label">
                  Name <span className="text-danger">*</span>
                </label>
                <input
                  name="name"
                  type="text"
                  className="form-control"
                  value={formData.name}
                  onChange={inputChange}
                  disabled={submitting}
                />
                {formError.name && (
                  <div className="invalid-feedback d-block text-danger">
                    {formError.name}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label className="col-form-label">
                  Description <span className="text-danger">*</span>
                </label>
                <textarea
                  name="description"
                  className="form-control"
                  style={{ height: "120px" }}
                  value={formData.description}
                  onChange={inputChange}
                  disabled={submitting}
                />
                {formError.description && (
                  <div className="invalid-feedback d-block text-danger">
                    {formError.description}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label className="col-form-label">
                  Select Parameter Fields <span className="text-danger">*</span>
                </label>
                <Select
                  isMulti
                  className="tagging"
                  classNamePrefix="react-select"
                  options={paramList.map((b) => ({
                    value: b.id,
                    label: b.name,
                  }))}
                  value={paramList
                    .filter((b) => formData.parameter_field_id.includes(b.id))
                    .map((b) => ({ value: b.id, label: b.name }))}
                  onChange={(selected) => {
                    setFormError((prev) => {
                      const updated = { ...prev };
                      delete updated.parameter_field_id;
                      return updated;
                    });
                    setFormData((prev) => ({
                      ...prev,
                      parameter_field_id:
                        selected?.map((opt) => opt.value) ?? [],
                    }));
                  }}
                  isClearable
                  isDisabled={submitting}
                />
                {formError.parameter_field_id && (
                  <div className="invalid-feedback d-block text-danger">
                    {formError.parameter_field_id}
                  </div>
                )}
              </div>

              <div className="d-flex justify-content-end">
                <button
                  type="button"
                  className="btn btn-soft-primary me-2"
                  onClick={() => setShowForm(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-soft-success"
                  disabled={submitting}
                >
                  {submitting
                    ? selectedId
                      ? "Updating..."
                      : "Creating..."
                    : selectedId
                    ? "Update"
                    : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>

        <Success
          id="CategorySuccessModal"
          title="Category"
          message={showMessage}
          show={showSuccess}
          onClose={() => setShowSuccess(false)}
        />

        <Delete
          show={showDeleteModal}
          title="Delete Category?"
          message="Are you sure?"
          onConfirm={confirmDelete}
          onCancel={() => setShowDeleteModal(false)}
        />

        <Delete
          show={showDeleteFailModal}
          title="Fail To Delete"
          message={showMessage}
          onConfirm={confirmDelete}
          onCancel={() => setShowDeleteFailModal(false)}
        />
      </div>
    </div>
  );
};

export default AddUpdateCategory;
